import { callFrappeMethod, isAuthenticationError } from "./session_api.mjs";

export const COMMENT_METHOD = "vatiosite.website.blog.engagement.add_comment";
export const LIKE_METHOD = "blog.templates.includes.likes.likes.like";
export const COMMENT_DRAFT_PREFIX = "vl-blog-comment-draft:";

export function normalizeEngagementCount(value) {
  const count = Number.parseInt(String(value ?? "0"), 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

export function nextLikeCount(count, wasLiked, isLiked) {
  const normalized = normalizeEngagementCount(count);
  if (wasLiked === isLiked) return normalized;
  return Math.max(0, normalized + (isLiked ? 1 : -1));
}

export function validComment(value) {
  return String(value || "").trim().length > 0;
}

export function resolveCommentAccess(authenticated) {
  return {
    showForm: authenticated === true,
    showLogin: authenticated === false,
  };
}

export function commentDraftKey(referenceName) {
  return `${COMMENT_DRAFT_PREFIX}${String(referenceName || "").trim()}`;
}

export function readCommentDraft(storage, referenceName) {
  try {
    return String(storage?.getItem(commentDraftKey(referenceName)) || "");
  } catch (error) {
    return "";
  }
}

export function writeCommentDraft(storage, referenceName, value) {
  try {
    const key = commentDraftKey(referenceName);
    const draft = String(value || "");
    if (draft) storage?.setItem(key, draft);
    else storage?.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
}

function setStatus(root, message, state = "") {
  const status = root.querySelector("[data-blog-comment-status]");
  if (!status) return;
  status.textContent = message || "";
  status.dataset.state = state;
}

function setLikeState(button, active, count) {
  button.setAttribute("aria-pressed", active ? "true" : "false");
  button.classList.toggle("is-active", active);
  button.setAttribute(
    "aria-label",
    active ? button.dataset.labelActive : button.dataset.labelDefault
  );
  const label = button.querySelector("[data-blog-like-label]");
  if (label) {
    label.textContent = active
      ? button.dataset.textActive
      : button.dataset.textDefault;
  }
  const countElement = button.querySelector("[data-blog-like-count]");
  if (countElement)
    countElement.textContent = String(normalizeEngagementCount(count));
}

function insertComment(root, html) {
  const list = root.querySelector("[data-blog-comment-list]");
  if (!list || !html) return null;
  const template = document.createElement("template");
  template.innerHTML = String(html).trim();
  const comment = template.content.firstElementChild;
  if (!comment) return null;
  comment.setAttribute("tabindex", "-1");
  list.prepend(comment);
  root.querySelector("[data-blog-comments-empty]")?.setAttribute("hidden", "");
  comment.focus();
  comment.addEventListener("blur", () => comment.removeAttribute("tabindex"), {
    once: true,
  });
  return comment;
}

function updateCommentCount(root, count) {
  const normalized = normalizeEngagementCount(count);
  root.querySelectorAll("[data-blog-comment-count]").forEach((element) => {
    element.textContent = String(normalized);
  });
  const control = root.querySelector(".vs-blog-comment-count");
  const label = control?.querySelector("[data-blog-comment-count-label]");
  if (label) {
    label.textContent =
      normalized === 1
        ? control.dataset.labelSingular
        : control.dataset.labelPlural;
  }
}

function applyCommentAccess(root, authenticated, expired = false) {
  if (typeof authenticated !== "boolean") return;
  const form = root.querySelector("[data-blog-comment-form]");
  const login = root.querySelector("[data-blog-comment-login]");
  const access = resolveCommentAccess(authenticated);
  if (form) form.hidden = !access.showForm;
  if (login) login.hidden = !access.showLogin;
  root.dataset.authenticated = authenticated ? "1" : "0";
  if (expired) {
    const status = root.querySelector("[data-blog-comment-status]");
    setStatus(root, status?.dataset.sessionExpired || "", "error");
  }
}

export function updateBlogEngagementSession(
  scope,
  authenticated,
  { expired = false } = {}
) {
  if (typeof authenticated !== "boolean") return;
  scope.querySelectorAll("[data-blog-engagement]").forEach((root) => {
    applyCommentAccess(root, authenticated, expired);
  });
}

function bindLike(root, callMethod) {
  const button = root.querySelector("[data-blog-like]");
  if (!button) return;
  let active = button.getAttribute("aria-pressed") === "true";
  let count = normalizeEngagementCount(
    button.querySelector("[data-blog-like-count]")?.textContent
  );
  setLikeState(button, active, count);

  button.addEventListener("click", async () => {
    if (button.dataset.pending === "1") return;
    button.dataset.pending = "1";
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    setStatus(root, "");
    try {
      const response = await callMethod(LIKE_METHOD, {
        reference_doctype: root.dataset.referenceDoctype,
        reference_name: root.dataset.referenceName,
        like: !active,
        route: root.dataset.route,
      });
      const nextActive = response?.message === true || response?.message === 1;
      count = nextLikeCount(count, active, nextActive);
      active = nextActive;
      setLikeState(button, active, count);
    } catch (error) {
      const status = root.querySelector("[data-blog-comment-status]");
      setStatus(root, status?.dataset.likeError || "", "error");
    } finally {
      delete button.dataset.pending;
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  });
}

function bindCommentForm(root, callMethod, storage) {
  const form = root.querySelector("[data-blog-comment-form]");
  if (!form) return;
  const textarea = form.elements.comment;
  const submit = form.querySelector("[data-blog-comment-submit]");
  const originalLabel = submit.textContent;
  const referenceName = root.dataset.referenceName;
  const draft = readCommentDraft(storage, referenceName);
  if (draft && !textarea.value) textarea.value = draft;

  textarea.addEventListener("input", () => {
    textarea.setCustomValidity("");
    writeCommentDraft(storage, referenceName, textarea.value);
  });
  textarea.addEventListener("keydown", (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
      event.preventDefault();
      form.requestSubmit();
    }
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    textarea.setCustomValidity(
      validComment(textarea.value) ? "" : form.dataset.emptyMessage
    );
    if (!form.reportValidity()) return;

    submit.disabled = true;
    submit.textContent = submit.dataset.labelPending;
    form.setAttribute("aria-busy", "true");
    setStatus(root, "");
    try {
      const response = await callMethod(COMMENT_METHOD, {
        reference_name: referenceName,
        comment: textarea.value.trim(),
      });
      const result = response?.message;
      if (!insertComment(root, result?.html))
        throw new Error("Missing comment response");
      updateCommentCount(root, result.comment_count);
      textarea.value = "";
      writeCommentDraft(storage, referenceName, "");
      const status = root.querySelector("[data-blog-comment-status]");
      setStatus(root, status?.dataset.success || "", "success");
    } catch (error) {
      const status = root.querySelector("[data-blog-comment-status]");
      if (isAuthenticationError(error)) {
        applyCommentAccess(root, false, true);
      } else if (error?.status === 429) {
        setStatus(root, status?.dataset.rateError || "", "error");
      } else if (!error?.status) {
        setStatus(root, status?.dataset.networkError || "", "error");
      } else {
        setStatus(root, status?.dataset.error || "", "error");
      }
    } finally {
      form.removeAttribute("aria-busy");
      submit.disabled = false;
      submit.textContent = originalLabel;
    }
  });

  if (
    root.dataset.authenticated === "1" &&
    globalThis.location?.hash === "#comments"
  ) {
    globalThis.requestAnimationFrame?.(() =>
      textarea.focus({ preventScroll: true })
    );
  }
}

function getSessionStorage() {
  try {
    return globalThis.window?.sessionStorage || null;
  } catch (error) {
    return null;
  }
}

export function initBlogEngagement(
  scope = document,
  { callMethod = callFrappeMethod, storage = getSessionStorage() } = {}
) {
  scope.querySelectorAll("[data-blog-engagement]").forEach((root) => {
    if (root.dataset.blogEngagementBound === "1") return;
    root.dataset.blogEngagementBound = "1";
    bindLike(root, callMethod);
    bindCommentForm(root, callMethod, storage);
  });
}
